import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import ManagementHeader from './ManagmentHeader';
import StudentTable from './StudentTable';
import StudentRemovalModal from './StudentRemovalModal';
import AddStudentModal from '../attendance/AddStudentModal';
import studentsAPI from '../../api/studentsAPI';
import instructorsAPI from '../../api/instructorsAPI';
import periodsAPI from '../../api/periodsAPI';
import { AttendanceAPI } from '../../api/attendanceAPI';
import { getCurrentTimezoneConfig, getResolvedTimezone } from '../../utils/timezoneUtils';
import { 
  convertPeriodTimeToSchoolTimezone, 
  convertPeriodTimeToUserTimezone
} from '../../utils/periodTimezoneUtils';
import { useSchoolTimezone } from '../../hooks/useSchoolTimezone';

export default function ClassDetail({ activeClass, onAddStudent, onTakeAttendance, onUpdateClass, onCreatePeriod, onUpdatePeriod, onDeletePeriod }) {
  const { getAccessTokenSilently } = useAuth0();
  const { currentSchool, currentOrganization } = useTenant();
  const { schoolTimezone, displayPeriodTime } = useSchoolTimezone();
  const navigate = useNavigate();
  
  // Helper function to parse date-only strings without timezone conversion
  // but still format them according to user's timezone preference
  const formatDateOnly = (dateString) => {
    if (!dateString) return null;
    
    // Extract date part if it's an ISO string with time
    const dateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [year, month, day] = dateOnly.split('-');
    
    // Create date object without timezone conversion
    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    // Format according to user's timezone preference
    const currentConfig = getCurrentTimezoneConfig();
    const timezone = getResolvedTimezone();
    
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    // Apply timezone only if not LOCAL (which should use device timezone)
    if (timezone && currentConfig !== 'LOCAL') {
      options.timeZone = timezone;
    }
    
    return dateObj.toLocaleDateString('en-US', options);
  };
  
  // Helper function to format timestamps with timezone awareness
  const formatTimestampWithTimezone = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const currentConfig = getCurrentTimezoneConfig();
    
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    // Apply timezone only if not LOCAL (which should use device timezone)
    if (currentConfig && currentConfig !== 'LOCAL') {
      options.timeZone = currentConfig;
    }
    
    return date.toLocaleString('en-US', options);
  };
  
  // activeClass.periods is an array: [{ name, reports, students }, ...]
  const [selectedIdx, setSelectedIdx] = useState(0);
  
  // Reports state for real attendance data
  const [attendanceReports, setAttendanceReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  
  // Student management state
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Instructors (for instructor switching functionality)
  const [instructors, setInstructors] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [instructorsError, setInstructorsError] = useState(null);

  // Instructor change overlay state
  const [showInstructorOverlay, setShowInstructorOverlay] = useState(false);
  const [switchingInstructor, setSwitchingInstructor] = useState(false);
  const [instructorSearchQuery, setInstructorSearchQuery] = useState('');

  // Previous class ID tracking to prevent unnecessary resets
  const [previousClassId, setPreviousClassId] = useState(null);

  // Student removal modal state
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [studentsToRemove, setStudentsToRemove] = useState([]);

  // Period management state
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [periodForm, setPeriodForm] = useState({
    name: '',
    startTime: '',
    endTime: ''
  });

  // Modal state & form data
  const [showAddStudent, setShowAddStudent] = useState(false);

  // Helper function to check if this is a joint class
  const isJointClass = activeClass?._dbClassroom?.is_joint_class || false;

  // Handle clicking on a report to navigate to AttendanceReportsPage
  const handleReportClick = (report) => {
    // Extract the date from the report - use the date part for URL parameter
    const dateOnly = report.date.split('T')[0]; // Get "2025-09-02"
    const formattedDate = dateOnly; // Use the ISO date format directly for URL
    
    // Use the same classroom ID pattern used elsewhere in this component
    const classroomId = activeClass._dbClassroom?.id || activeClass.id;
    
    // Navigate to AttendanceReportsPage with query parameters to pre-select the report
    // Use the correct route pattern with school context
    const schoolId = currentSchool?.unique_id || currentSchool?.unique_id;
    navigate(`/org/${currentOrganization?.unique_id}/school/${schoolId}/attendance-reports?date=${formattedDate}&class=${classroomId}&reportId=${report.id}`);
  };

  // Helper function to render joint class badge
  const renderJointClassBadge = () => {
    if (!isJointClass) return null;
    return (
      <span className="inline-flex items-center px-2 py-1 ml-2 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
        Joint Class
      </span>
    );
  };

  // Reset all component state when activeClass ID changes (when switching schools/classes)
  // Use state to track the previous class ID to avoid resetting on data refreshes
  useEffect(() => {
    const currentClassId = activeClass?._dbClassroom?.id || activeClass?.id;
    
    // Only reset if the class ID actually changed, not just the object reference
    if (currentClassId !== previousClassId) {
      setPreviousClassId(currentClassId);
      
      // Reset period selection
      setSelectedIdx(0);
      
      // Reset data states - CRITICAL for clearing old school data
      setAttendanceReports([]);
      setEnrolledStudents([]);
      setInstructors([]);
      setInstructorsError(null);
      
      // Reset loading states
      setLoadingReports(false);
      setLoadingStudents(false);
      setLoadingInstructors(false);
      
      // Reset modal and overlay states
      setShowAddStudent(false);
      setShowInstructorOverlay(false);
      setShowAddPeriod(false);
      setSwitchingInstructor(false);
      setEditingPeriodId(null);
      
      // Reset form states
      setPeriodForm({
        name: '',
        startTime: '',
        endTime: ''
      });
    }
  }, [activeClass, previousClassId]);

  // Load instructors for Assign Instructor dropdown (read-only wiring)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingInstructors(true);
        const token = await getAccessTokenSilently();
        
        // Get the school ID from the classroom to filter instructors by school access
        const schoolId = activeClass?._dbClassroom?.school_id || activeClass?.school_id;
        console.log(`Loading instructors for ClassDetail modal with schoolId: ${schoolId}`);
        
        const list = await instructorsAPI.getAllInstructors(token, { schoolId });
        if (!cancelled) {
          setInstructors(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error('Failed to load instructors', err);
        if (!cancelled) setInstructorsError(err);
      } finally {
        if (!cancelled) setLoadingInstructors(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAccessTokenSilently, activeClass?._dbClassroom?.synced_teacher_id, activeClass?._dbClassroom?.school_id, activeClass?.school_id]);

  // Filter instructors based on search query
  const filteredInstructors = useMemo(() => {
    if (!instructorSearchQuery.trim()) {
      return instructors;
    }
    
    const query = instructorSearchQuery.toLowerCase();
    return instructors.filter(instructor => 
      instructor.name?.toLowerCase().includes(query) ||
      instructor.email?.toLowerCase().includes(query)
    );
  }, [instructors, instructorSearchQuery]);

  // Close period dropdown menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close all period dropdown menus
      const periods = Array.isArray(activeClass?.periods) ? activeClass.periods : [];
      periods.forEach((_, i) => {
        const dropdown = document.getElementById(`period-menu-${i}`);
        if (dropdown && !dropdown.contains(event.target)) {
          dropdown.classList.add('hidden');
        }
      });
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeClass?.periods]);

  // Load enrolled students when component mounts or activeClass changes
  useEffect(() => {
    const loadStudents = async () => {
      const classroomId = activeClass?._dbClassroom?.id || activeClass?.id;
      // Early return and clear data if no activeClass
      if (!classroomId) {
        setEnrolledStudents([]);
        setLoadingStudents(false);
        return;
      }
      
      setLoadingStudents(true);
      try {
        const token = await getAccessTokenSilently();
        
        // Get the current period to determine if we should fetch period-specific students
        const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
        const currentPeriod = periods[selectedIdx] || {};
        
        console.log('📋 Loading students for classroom:', classroomId, 'period:', currentPeriod.id);
        
        let students;
        if (currentPeriod.id) {
          // Fetch students for the specific period
          students = await studentsAPI.getStudentsByPeriod(classroomId, currentPeriod.id, token);
          console.log('📚 Loaded period-specific students:', students);
        } else {
          // Fetch all students for the classroom (no period filtering)
          students = await studentsAPI.getStudentsByClassroom(classroomId, token);
          console.log('📚 Loaded all classroom students:', students);
        }
        
        setEnrolledStudents(students);
      } catch (error) {
        console.error('Failed to load students:', error);
        // Fall back to using static data from activeClass if API fails
        const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
        const currentPeriod = periods[selectedIdx] || {};
        const fallbackStudents = Array.isArray(currentPeriod.students) ? currentPeriod.students : [];
        setEnrolledStudents(fallbackStudents);
      } finally {
        setLoadingStudents(false);
      }
    };

    loadStudents();
  }, [activeClass?.id, activeClass?._dbClassroom?.id, activeClass?._dbClassroom?.school_id, activeClass?.school_id, activeClass?.periods, selectedIdx, getAccessTokenSilently]);

  // Load attendance reports when class or period changes
  useEffect(() => {
    const loadAttendanceReports = async () => {
      // Early return and clear data if no activeClass
      if (!activeClass?._dbClassroom?.id) {
        setAttendanceReports([]);
        setTotalReportsCount(0);
        setLoadingReports(false);
        return;
      }
      
      setLoadingReports(true);
      try {
        const token = await getAccessTokenSilently();
        const classroomId = activeClass._dbClassroom.id;
        
        // Get current period if available
        const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
        const currentPeriod = periods[selectedIdx];
        const periodId = currentPeriod?._dbPeriod?.id || null;
        
        const reports = await AttendanceAPI.getSessionsByClass(classroomId, periodId, token);
        
        // Store total count for "View All" button logic
        const totalCount = Array.isArray(reports) ? reports.length : 0;
        setTotalReportsCount(totalCount);
        
        // Limit to last 5 reports for performance (most recent first)
        const limitedReports = Array.isArray(reports) ? reports.slice(0, 5) : [];
        
        console.log('📊 ClassDetail: Loaded attendance reports:', {
          total: reports.length,
          displayed: limitedReports.length,
          sampleReport: limitedReports[0] ? {
            id: limitedReports[0].id,
            date: limitedReports[0].date,
            timestamp: limitedReports[0].timestamp,
            created_at: limitedReports[0].created_at,
            instructor: limitedReports[0].instructor
          } : null
        });
        
        setAttendanceReports(limitedReports);
      } catch (error) {
        console.error('Failed to load attendance reports:', error);
        setAttendanceReports([]);
        setTotalReportsCount(0);
      } finally {
        setLoadingReports(false);
      }
    };

    loadAttendanceReports();
  }, [activeClass?._dbClassroom?.id, activeClass?.periods, selectedIdx, getAccessTokenSilently]);

  // Helper function to reload students (can be called after enrollment changes)
  const reloadStudents = async () => {
    const classroomId = activeClass?._dbClassroom?.id || activeClass?.id;
    if (classroomId) {
      setLoadingStudents(true);
      try {
        const token = await getAccessTokenSilently();
        
        // Get the current period to determine if we should fetch period-specific students
        const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
        const currentPeriod = periods[selectedIdx] || {};
        
        console.log('🔄 Reloading students for classroom:', classroomId, 'period:', currentPeriod.id);
        
        let students;
        if (currentPeriod.id) {
          // Fetch students for the specific period
          students = await studentsAPI.getStudentsByPeriod(classroomId, currentPeriod.id, token);
          console.log('📚 Fetched period-specific students:', students);
        } else {
          // Fetch all students for the classroom (no period filtering)
          students = await studentsAPI.getStudentsByClassroom(classroomId, token);
          console.log('📚 Fetched all classroom students:', students);
        }
        
        setEnrolledStudents(students);
      } catch (error) {
        console.error('❌ Failed to reload students:', error);
      } finally {
        setLoadingStudents(false);
      }
    }
  };

  // Handle null activeClass or invalid activeClass (when onUpdateClass breaks the state)
  if (!activeClass || typeof activeClass !== 'object' || !activeClass.name) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 flex flex-col h-[900px]">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-600 text-lg mb-4">
              {typeof activeClass === 'number' 
                ? 'Class data is being refreshed. Please refresh the page if this persists.'
                : 'No classes have been created for this school yet.'
              }
            </div>
            <div className="text-gray-500 text-sm">
              {typeof activeClass === 'number' 
                ? 'The instructor change was successful, but the class data needs to be refreshed.'
                : 'Please create a class using the "Manage Classes" option in the sidebar.'
              }
            </div>
            {typeof activeClass === 'number' && (
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-blue-700 transition"
              >
                Refresh Page
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Period management functions
  const handleOpenAddPeriod = () => {
    setPeriodForm({ name: '', startTime: '', endTime: '' });
    setEditingPeriodId(null);
    setShowAddPeriod(true);
  };

  const handleCloseAddPeriod = () => {
    setShowAddPeriod(false);
    setEditingPeriodId(null);
    setPeriodForm({ name: '', startTime: '', endTime: '' });
  };

  const validateTimeFormat = (time) => {
    const timeRegex = /^(0?[1-9]|1[0-2]):([0-5][0-9])(AM|PM)$/i;
    return timeRegex.test(time);
  };

  const convertTimeToAPIFormat = (timeString) => {
    // Convert "10:00AM" to "10:00:00" format
    // Now uses school timezone context for proper storage
    const time = timeString.replace(/AM|PM/i, '').trim();
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    
    if (timeString.toUpperCase().includes('PM') && hour24 !== 12) {
      hour24 += 12;
    } else if (timeString.toUpperCase().includes('AM') && hour24 === 12) {
      hour24 = 0;
    }
    
    const formattedTime = `${hour24.toString().padStart(2, '0')}:${minutes}`;
    
    // Convert from user's display timezone to school timezone for storage
    return convertPeriodTimeToSchoolTimezone(formattedTime, schoolTimezone);
  };

  const convertTimeFromAPIFormat = (timeString) => {
    // Convert "10:00:00" to "10:00AM" format
    // Now uses school timezone context for proper display
    if (!timeString) return '';
    
    // Convert from school timezone to user's display timezone
    const userTime = convertPeriodTimeToUserTimezone(timeString, schoolTimezone);
    const [hours, minutes] = userTime.split(':');
    let hour12 = parseInt(hours);
    const isPM = hour12 >= 12;
    
    if (hour12 > 12) {
      hour12 -= 12;
    } else if (hour12 === 0) {
      hour12 = 12;
    }
    
    return `${hour12}:${minutes}${isPM ? 'PM' : 'AM'}`;
  };

  const handleSubmitPeriod = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!periodForm.name.trim()) {
      alert('Please enter a period name');
      return;
    }
    
    if (!periodForm.startTime.trim() || !periodForm.endTime.trim()) {
      alert('Please enter both start and end times');
      return;
    }
    
    if (!validateTimeFormat(periodForm.startTime)) {
      alert('Please enter start time in format: 10:00AM');
      return;
    }
    
    if (!validateTimeFormat(periodForm.endTime)) {
      alert('Please enter end time in format: 10:00AM');
      return;
    }

    try {
      // Get classroom ID with fallback logic
      const classroomId = activeClass._dbClassroom?.id || activeClass.id;
      
      if (!classroomId) {
        alert('Cannot save period: Classroom ID not found. Please refresh the page and try again.');
        return;
      }
      
      if (editingPeriodId) {
        // Update existing period
        if (onUpdatePeriod) {
          const periodData = {
            name: periodForm.name.trim(),
            start_time: convertTimeToAPIFormat(periodForm.startTime),
            end_time: convertTimeToAPIFormat(periodForm.endTime),
            day_of_week: 1, // Default to Monday - you could add day selection to the form
            classroom_id: classroomId
          };
          
          await onUpdatePeriod(editingPeriodId, periodData);
          handleCloseAddPeriod();
        } else {
          alert('Cannot update period: Handler not available');
        }
      } else {
        // Add new period
        if (onCreatePeriod) {
          const periodData = {
            name: periodForm.name.trim(),
            start_time: convertTimeToAPIFormat(periodForm.startTime),
            end_time: convertTimeToAPIFormat(periodForm.endTime),
            day_of_week: 1, // Default to Monday - you could add day selection to the form
            period_number: activeClass.periods?.length ? activeClass.periods.length + 1 : 1,
            classroom_id: classroomId,
            // Inherit the classroom's assigned teacher
            synced_teacher_id: activeClass._dbClassroom?.synced_teacher_id || null
          };
          
          await onCreatePeriod(classroomId, periodData);
          handleCloseAddPeriod();
        } else {
          alert('Cannot create period: Handler not available');
        }
      }
    } catch (error) {
      console.error('Error saving period:', error);
      alert(`Failed to save period: ${error.message}`);
    }
  };

  // Instructor management functions
  const handleOpenInstructorOverlay = () => {
    setShowInstructorOverlay(true);
  };

  const handleCloseInstructorOverlay = () => {
    setShowInstructorOverlay(false);
    setInstructorSearchQuery(''); // Clear search when closing
  };

  const handleSwitchInstructor = async (newInstructor) => {
    if (!newInstructor) {
      console.error('Missing instructor data for switch');
      return;
    }

    const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
    const currentPeriod = periods[selectedIdx];
    
    // We should only work with periods for instructor assignments
    if (!currentPeriod?.id) {
      alert('Please create a period first before assigning an instructor. Instructor assignments are managed at the period level.');
      return;
    }
    
    setSwitchingInstructor(true);
    try {
      const token = await getAccessTokenSilently();
      
      // Always update the period instructor (this is where instructor data should be stored)
      await periodsAPI.assignTeacherToPeriod(currentPeriod.id, newInstructor.id, token);
      
      // Refresh the classroom data to get updated periods with teacher info
      const classroomId = activeClass._dbClassroom?.id || activeClass.id;
      
      if (classroomId && onUpdateClass) {
        await onUpdateClass(classroomId);
      }
      
      handleCloseInstructorOverlay();
      
    } catch (error) {
      console.error('Failed to switch instructor:', error);
      alert(`Failed to switch instructor: ${error.message}`);
    } finally {
      setSwitchingInstructor(false);
    }
  };

  // Get periods array, guard if missing
  const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
  if (!periods.length) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 flex flex-col h-[900px]">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <h2 className="text-xl font-bold text-slate-800">
              {activeClass.name} – No Periods
            </h2>
            {renderJointClassBadge()}
          </div>
        </div>

        {/* Instructor Header */}
        {(() => {
          // For classes with no periods, show classroom instructor but disable instructor changes
          const classroomInstructor = activeClass?._dbClassroom?.synced_teacher;
          
          return (
            <div>
              <ManagementHeader
                name={classroomInstructor?.name || 'Default Instructor (Create Period First)'}
                email={classroomInstructor?.email || ''}
                image={classroomInstructor?.image || classroomInstructor?.imageUrl || classroomInstructor?.image_url || ''}
                onChangeInstructor={() => alert('Please create a period first. Instructor assignments are managed at the period level.')}
                isLoading={switchingInstructor}
              />
              {classroomInstructor && (
                <p className="text-sm text-gray-600 mt-2 px-4">
                  This instructor will be assigned to new periods by default
                </p>
              )}
            </div>
          );
        })()}
        
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-600 text-lg mb-4">No periods have been created for this class yet.</div>
            <button 
              onClick={handleOpenAddPeriod}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-blue-700 transition"
            >
              + Add First Period
            </button>
          </div>
        </div>

        {/* Add Period Modal */}
        {showAddPeriod && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative">
              <button
                aria-label="Close"
                onClick={handleCloseAddPeriod}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
              
              <h2 className="text-xl font-bold mb-6 text-slate-800">
                {editingPeriodId ? 'Edit Period' : 'Add New Period'}
              </h2>
              
              <form onSubmit={handleSubmitPeriod}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Period Name</label>
                  <input
                    type="text"
                    value={periodForm.name}
                    onChange={(e) => setPeriodForm({ ...periodForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600"
                    placeholder="e.g., 1st Period, Morning Session"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Start Time
                    {currentSchool?.timezone && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Will be stored in {currentSchool.timezone.split('/')[1]?.replace('_', ' ') || currentSchool.timezone} time)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={periodForm.startTime}
                    onChange={(e) => setPeriodForm({ ...periodForm, startTime: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600"
                    placeholder="e.g., 9:00AM"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    End Time
                    {currentSchool?.timezone && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Will be stored in {currentSchool.timezone.split('/')[1]?.replace('_', ' ') || currentSchool.timezone} time)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={periodForm.endTime}
                    onChange={(e) => setPeriodForm({ ...periodForm, endTime: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600"
                    placeholder="e.g., 10:00AM"
                  />
                </div>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                    onClick={handleCloseAddPeriod}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {editingPeriodId ? 'Update Period' : 'Add Period'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Instructor Change Overlay */}
        {showInstructorOverlay && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg relative max-h-[80vh] overflow-hidden">
              <button
                aria-label="Close"
                onClick={handleCloseInstructorOverlay}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl z-10"
              >
                ×
              </button>
              
              <div className="p-6">
                <h2 className="text-xl font-bold mb-6 text-slate-800">Change Instructor</h2>
                <p className="text-gray-600 mb-4">Select a new instructor for this class:</p>
                
                {/* Search Input */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search instructors by name or email..."
                    value={instructorSearchQuery}
                    onChange={(e) => setInstructorSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setInstructorSearchQuery('');
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  {instructorSearchQuery && (
                    <p className="text-sm text-gray-500 mt-1">
                      Showing {filteredInstructors.length} of {instructors.length} instructors
                    </p>
                  )}
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {loadingInstructors ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading instructors...</p>
                    </div>
                  ) : instructorsError ? (
                    <div className="text-center py-4">
                      <p className="text-red-500">Failed to load instructors</p>
                    </div>
                  ) : filteredInstructors.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">
                        {instructorSearchQuery ? 'No instructors found matching your search' : 'No instructors available'}
                      </p>
                      {instructorSearchQuery && (
                        <button
                          onClick={() => setInstructorSearchQuery('')}
                          className="text-blue-600 hover:text-blue-800 text-sm mt-2"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredInstructors.map((instructor) => {
                        const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
                        const currentPeriod = periods[selectedIdx];
                        
                        // Use the same logic as the display: period instructor > classroom instructor
                        const currentInstructor = currentPeriod?.synced_teacher || 
                                                activeClass?._dbClassroom?.synced_teacher;
                        
                        const isCurrentInstructor = instructor.id === currentInstructor?.id;
                        
                        return (
                          <div
                            key={instructor.id}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              isCurrentInstructor
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => !isCurrentInstructor && handleSwitchInstructor(instructor)}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                {instructor.image || instructor.imageUrl || instructor.image_url ? (
                                  <img
                                    src={instructor.image || instructor.imageUrl || instructor.image_url}
                                    alt={instructor.name}
                                    className="w-8 h-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-gray-600 text-sm">
                                    {instructor.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{instructor.name}</p>
                                {instructor.email && (
                                  <p className="text-sm text-gray-500">{instructor.email}</p>
                                )}
                              </div>
                              {isCurrentInstructor && (
                                <span className="text-blue-600 text-sm font-medium">Current</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {switchingInstructor && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-600 mt-2">Switching instructor...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Pick the current period's data
  const current = periods[selectedIdx] || {};
  const periodName = current.name || 'Unknown Period';
  
  // Use enrolledStudents from API instead of static period students
  // But show loading state while fetching
  const studentsToDisplay = loadingStudents 
    ? [] 
    : enrolledStudents.length > 0 
      ? enrolledStudents 
      : Array.isArray(current.students) ? current.students : [];

  // Use fetched attendance reports (limited to 5 for performance)
  const displayedReports = attendanceReports;

  // Handle navigation to full attendance reports page
  const handleViewAllReports = () => {
    // Navigate to attendance reports page with tenant context
    const currentPeriod = periods[selectedIdx];
    const classId = activeClass._dbClassroom?.id || activeClass.id;
    const periodId = currentPeriod?.id;
    const schoolId = currentSchool?.unique_id || currentSchool?.unique_id;
    
    // Use tenant-aware navigation with proper URL structure
    // Pass date=all to indicate we want to see all reports regardless of date
    if (periodId) {
      const targetUrl = `/org/${currentOrganization?.unique_id}/school/${schoolId}/attendance-reports?date=all&class=${classId}&period=${periodId}`;
      navigate(targetUrl);
    } else {
      const targetUrl = `/org/${currentOrganization?.unique_id}/school/${schoolId}/attendance-reports?date=all&class=${classId}`;
      navigate(targetUrl);
    }
  };

  // Handle navigation to Take Attendance page with pre-selected class and period
  const handleTakeAttendance = () => {
    const currentPeriod = periods[selectedIdx];
    const classId = activeClass._dbClassroom?.id || activeClass.id;
    const periodId = currentPeriod?.id;
    const schoolId = currentSchool?.unique_id || currentSchool?.unique_id;
    
    // Navigate to Take Attendance page with query parameters to pre-select class and period
    if (periodId && classId && schoolId) {
      const targetUrl = `/org/${currentOrganization?.unique_id}/school/${schoolId}/take-attendance?class=${classId}&period=${periodId}`;
      navigate(targetUrl);
    } else if (classId && schoolId) {
      // If no period is available, just pre-select the class
      const targetUrl = `/org/${currentOrganization?.unique_id}/school/${schoolId}/take-attendance?class=${classId}`;
      navigate(targetUrl);
    } else {
      // Fallback to basic take attendance page
      const targetUrl = `/org/${currentOrganization?.unique_id}/school/${schoolId}/take-attendance`;
      navigate(targetUrl);
    }
  };

  // Handlers for Add Student modal
  const handleOpenAddStudent = () => {
    setShowAddStudent(true);
  };

  const handleCloseAddStudent = () => {
    setShowAddStudent(false);
  };

  const handleNewStudentCreated = async (newStudents) => {
    // Reload students to get the updated list
    await reloadStudents();
  };

  const handleExistingStudentsEnrolled = async (enrolledStudents) => {
    // Reload students to get the updated list
    await reloadStudents();
  };

  // Handle opening removal modal for single student
  const handleRemoveStudent = (student) => {
    setStudentsToRemove([student]);
    setShowRemovalModal(true);
  };

  // Handle opening removal modal for multiple students
  const handleBulkRemoveStudents = (students) => {
    setStudentsToRemove(students);
    setShowRemovalModal(true);
  };

  // Handle actual removal after modal confirmation
  const handleConfirmRemoval = async (students, removalType) => {
    try {
      const token = await getAccessTokenSilently();
      const classroomId = activeClass._dbClassroom?.id || activeClass.id;
      const current = periods[selectedIdx] || {};
      const periodId = current.id;

      for (const student of students) {
        const studentId = student.student_id || student.id;
        
        if (removalType === 'period') {
          if (!periodId) {
            throw new Error('Period ID not found');
          }
          await studentsAPI.unenrollStudentFromPeriod(studentId, classroomId, periodId, token);
        } else {
          // Remove from entire class
          await studentsAPI.unenrollStudent(studentId, classroomId, token);
        }
      }
      
      // Update local state to remove the students
      const removedStudentIds = students.map(s => s.student_id || s.id);
      setEnrolledStudents(prev => 
        prev.filter(s => !removedStudentIds.includes(s.student_id || s.id))
      );
      
    } catch (error) {
      console.error('Error removing students:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 flex flex-col h-[1120px]">
      {/* Title */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <h2 className="text-xl font-bold text-slate-800">
            {activeClass.name} – {periodName}
          </h2>
          {renderJointClassBadge()}
        </div>
        
        {/* Class Instructor Info */}
        {(() => {
          // Get the class instructor from T_ATT_CLASSROOM (not period instructor)
          const classInstructor = activeClass?._dbClassroom?.synced_teacher;
          
          if (classInstructor) {
            return (
              <div className="text-right">
                <div className="text-sm font-medium text-slate-700">
                  Primary Instructor: {classInstructor.name} {/*({classInstructor.email})*/}
                </div>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Period Tabs */}
      <div className="flex items-center space-x-4 border-b pb-2 mb-6">
        {periods.map((p, idx) => (
          <div key={p.id || p.name} className="relative">
            <button
              onClick={() => setSelectedIdx(idx)}
              className={`px-4 py-3 rounded-t-lg transition-all duration-200 ${
                idx === selectedIdx
                  ? 'bg-blue-50 border-b-2 border-blue-600 text-blue-600 font-medium'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center space-x-2">
                <div className="text-center">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs mt-1 opacity-75">
                    {(p.startTime && p.endTime) || (p.start_time && p.end_time) ? 
                      displayPeriodTime(
                        p.startTime || p.start_time,
                        p.endTime || p.end_time
                      ) : 
                      'No time set'}
                  </div>
                </div>
                
                {/* Three-dot menu button positioned to the right of period content */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Toggle dropdown for this period
                      const dropdownId = `period-menu-${idx}`;
                      const dropdown = document.getElementById(dropdownId);
                      if (dropdown) {
                        dropdown.classList.toggle('hidden');
                        // Close other dropdowns
                        periods.forEach((_, i) => {
                          if (i !== idx) {
                            const otherDropdown = document.getElementById(`period-menu-${i}`);
                            if (otherDropdown) {
                              otherDropdown.classList.add('hidden');
                            }
                          }
                        });
                      }
                    }}
                    className="p-2 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                  
                  {/* Dropdown menu */}
                  <div
                    id={`period-menu-${idx}`}
                    className="hidden absolute right-0 top-6 w-32 bg-white rounded-lg shadow-lg border z-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => {
                        // Handle edit period
                        setPeriodForm({
                          name: p.name,
                          startTime: convertTimeFromAPIFormat(p.start_time) || '',
                          endTime: convertTimeFromAPIFormat(p.end_time) || ''
                        });
                        setEditingPeriodId(p.id);
                        setShowAddPeriod(true);
                        document.getElementById(`period-menu-${idx}`).classList.add('hidden');
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded-t-lg"
                    >
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Are you sure you want to delete period "${p.name}"?`)) {
                          try {
                            // Hide dropdown before deletion to prevent null reference error
                            const dropdown = document.getElementById(`period-menu-${idx}`);
                            if (dropdown) {
                              dropdown.classList.add('hidden');
                            }
                            await onDeletePeriod(p.id);
                          } catch (error) {
                            alert('Failed to delete period: ' + error.message);
                          }
                        } else {
                          // If user cancelled, still hide the dropdown
                          const dropdown = document.getElementById(`period-menu-${idx}`);
                          if (dropdown) {
                            dropdown.classList.add('hidden');
                          }
                        }
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </button>
          </div>
        ))}
        
        {/* Add Period Button */}
        <button 
          onClick={handleOpenAddPeriod}
          className="px-3 py-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
          title="Add new period"
        >
          <div className="flex items-center space-x-1">
            <span className="text-lg">+</span>
            <span className="text-sm">Period</span>
          </div>
        </button>
      </div>

      {/* Instructor Header */}
      {(() => {
        const periods = Array.isArray(activeClass.periods) ? activeClass.periods : [];
        const currentPeriod = periods[selectedIdx];
        
        // Priority: period instructor > classroom instructor
        const currentInstructor = currentPeriod?.synced_teacher || 
                                activeClass?._dbClassroom?.synced_teacher;
        
        return (
          <ManagementHeader
            name={currentInstructor?.name || 'No Instructor Assigned'}
            email={currentInstructor?.email || ''}
            image={currentInstructor?.image || currentInstructor?.imageUrl || currentInstructor?.image_url || ''}
            onChangeInstructor={handleOpenInstructorOverlay}
            isLoading={switchingInstructor}
          />
        );
      })()}

      {/* Content: Reports & StudentTable */}
      <div className="flex flex-1 space-x-6 mt-6 overflow-hidden">
        {/* Reports Panel */}
        <div className="w-1/4 flex flex-col space-y-4 overflow-y-auto p-4 bg-slate-50 rounded-lg">
          <h2 className="text-medium font-bold text-slate-800 mb-2">
            {activeClass.name} – {periodName} Reports
          </h2>
          
          {loadingReports ? (
            <div className="p-3 bg-white rounded-lg">
              <div className="text-sm text-slate-500">Loading reports...</div>
            </div>
          ) : displayedReports.length === 0 ? (
            <div className="p-3 bg-white rounded-lg">
              <div className="text-sm text-slate-500">No attendance reports yet</div>
            </div>
          ) : (
            displayedReports.map(rep => (
              <div
                key={rep.id}
                onClick={() => handleReportClick(rep)}
                className="p-3 bg-white rounded-lg hover:bg-slate-100 cursor-pointer transition-colors border border-transparent hover:border-blue-200 group"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-slate-800 group-hover:text-blue-600 transition-colors">
                      {rep.instructor}
                    </div>
                    <div className="text-sm text-slate-500">
                      {formatDateOnly(rep.date)} • {rep.wasUpdated ? 
                        `Updated at ${formatTimestampWithTimezone(rep.updatedAt)}` : 
                        `Created at ${formatTimestampWithTimezone(rep.created_at)}`
                      }
                    </div>
                  </div>
                  <svg 
                    className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors opacity-0 group-hover:opacity-100" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))
          )}

          {/* View All Reports button - only show if there are more than 5 reports */}
          {totalReportsCount > 5 && (
            <button
              onClick={handleViewAllReports}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
            >
              View All →
            </button>
          )}
        </div>

        {/* Student Table + Add Button */}
        <div className="flex-1 flex flex-col">
          {loadingStudents ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Students</h3>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="p-6 text-center text-gray-500">
                  Loading students...
                </div>
              </div>
            </>
          ) : studentsToDisplay.length > 0 ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-800">Students</h3>
              </div>
              <div className="flex-1 overflow-y-auto">
                <StudentTable
                  students={studentsToDisplay}
                  onTakeAttendance={handleTakeAttendance}
                  onRemoveStudent={handleRemoveStudent}
                  onBulkRemoveStudents={handleBulkRemoveStudents}
                  onAddStudent={handleOpenAddStudent}
                />
              </div>
            </>
          ) : (
            /* No Students Component */
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No students in this class</h3>
                <p className="text-gray-600 mb-4">
                  There are no students enrolled in {periods[selectedIdx]?.name || 'this period'} yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Add students to start taking attendance and managing your class.
                </p>
                <button
                  onClick={handleOpenAddStudent}
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow hover:bg-blue-700 transition mx-auto"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Add First Student
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Student Modal */}
      <AddStudentModal
        isOpen={showAddStudent}
        onClose={handleCloseAddStudent}
        classId={activeClass._dbClassroom?.id || activeClass.id}
        periods={periods}
        activePeriodId={periods[selectedIdx]?.id}
        onNewStudentCreated={handleNewStudentCreated}
        onExistingStudentsEnrolled={handleExistingStudentsEnrolled}
      />

      {/* Add Period Modal */}
      {showAddPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative">
            <button
              aria-label="Close"
              onClick={handleCloseAddPeriod}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-slate-800">
              Add New Period
            </h2>
            <form onSubmit={handleSubmitPeriod} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Period Name</label>
                <input
                  type="text"
                  value={periodForm.name}
                  onChange={e => setPeriodForm(s => ({ ...s, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="e.g., Period 1, Lunch, Study Hall"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Start Time</label>
                <input
                  type="text"
                  value={periodForm.startTime}
                  onChange={e => setPeriodForm(s => ({ ...s, startTime: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="10:00AM"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Format: 10:00AM or 2:30PM</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Time</label>
                <input
                  type="text"
                  value={periodForm.endTime}
                  onChange={e => setPeriodForm(s => ({ ...s, endTime: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600"
                  placeholder="10:50AM"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Format: 10:50AM or 3:20PM</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                  onClick={handleCloseAddPeriod}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Add Period
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Instructor Change Overlay */}
      {showInstructorOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg relative max-h-[80vh] overflow-hidden">
            <button
              aria-label="Close"
              onClick={handleCloseInstructorOverlay}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl z-10"
            >
              ×
            </button>
            
            <div className="p-6">
              <h2 className="text-xl font-bold mb-6 text-slate-800">Change Instructor</h2>
              <p className="text-gray-600 mb-4">Select a new instructor for this class:</p>
              
              {/* Search Input */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search instructors by name or email..."
                  value={instructorSearchQuery}
                  onChange={(e) => setInstructorSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setInstructorSearchQuery('');
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                {instructorSearchQuery && (
                  <p className="text-sm text-gray-500 mt-1">
                    Showing {filteredInstructors.length} of {instructors.length} instructors
                  </p>
                )}
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                {loadingInstructors ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading instructors...</p>
                  </div>
                ) : instructorsError ? (
                  <div className="text-center py-4">
                    <p className="text-red-500">Failed to load instructors</p>
                  </div>
                ) : filteredInstructors.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">
                      {instructorSearchQuery ? 'No instructors found matching your search' : 'No instructors available'}
                    </p>
                    {instructorSearchQuery && (
                      <button
                        onClick={() => setInstructorSearchQuery('')}
                        className="text-blue-600 hover:text-blue-800 text-sm mt-2"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredInstructors.map((instructor) => (
                      <div
                        key={instructor.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          instructor.id === activeClass?.instructor?.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => instructor.id !== activeClass?.instructor?.id && handleSwitchInstructor(instructor)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                            {instructor.image || instructor.imageUrl || instructor.image_url ? (
                              <img
                                src={instructor.image || instructor.imageUrl || instructor.image_url}
                                alt={instructor.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-600 text-sm">
                                {instructor.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{instructor.name}</p>
                            {instructor.email && (
                              <p className="text-sm text-gray-500">{instructor.email}</p>
                            )}
                          </div>
                          {instructor.id === activeClass?.instructor?.id && (
                            <span className="text-blue-600 text-sm font-medium">Current</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {switchingInstructor && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Switching instructor...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Student Removal Modal */}
      <StudentRemovalModal
        isOpen={showRemovalModal}
        onClose={() => setShowRemovalModal(false)}
        students={studentsToRemove}
        periodName={periods[selectedIdx]?.name || 'Unknown Period'}
        onConfirmRemoval={handleConfirmRemoval}
      />
    </div>
  );
}